'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Hl, Lesson, M, MB, NumberQuiz, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/calculus'

const ChainNudge = dynamic(() => import('../_components/widgets/CalcWidgets').then(m => m.ChainNudge), { ssr: false })

function Gears() {
  return (
    <svg viewBox="0 0 520 170" className="mx-auto block w-full max-w-[560px]" role="img" aria-label="Three gears: turning x turns u three times as fast, which turns y">
      <style>{`
        @keyframes gd-g1 { to { transform: rotate(360deg) } }
        @keyframes gd-g2 { to { transform: rotate(-1080deg) } }
        @keyframes gd-g3 { to { transform: rotate(540deg) } }
        @media (prefers-reduced-motion: reduce) { .gd-gear { animation: none !important } }
      `}</style>
      {[
        { cx: 95, r: 62, teeth: 18, color: 'var(--gd-blue)', anim: 'gd-g1 9s linear infinite', label: 'x' },
        { cx: 235, r: 24, teeth: 7, color: 'var(--gd-green)', anim: 'gd-g2 9s linear infinite', label: 'u' },
        { cx: 330, r: 48, teeth: 14, color: 'var(--gd-amber)', anim: 'gd-g3 9s linear infinite', label: 'y' },
      ].map(g => (
        <g key={g.label} className="gd-gear" style={{ transformOrigin: `${g.cx}px 85px`, animation: g.anim }}>
          {Array.from({ length: g.teeth }, (_, i) => {
            const a = (i / g.teeth) * Math.PI * 2
            return <rect key={i} x={g.cx - 4} y={85 - g.r - 9} width={8} height={12} rx={2} fill={g.color} transform={`rotate(${(a * 180) / Math.PI} ${g.cx} 85)`} />
          })}
          <circle cx={g.cx} cy={85} r={g.r} fill="var(--gd-surface)" stroke={g.color} strokeWidth={3} />
          <line x1={g.cx} y1={85} x2={g.cx} y2={85 - g.r + 6} stroke={g.color} strokeWidth={3} strokeLinecap="round" />
          <text x={g.cx} y={90} textAnchor="middle" fontSize={16} fontWeight={700} fill={g.color}>
            {g.label}
          </text>
        </g>
      ))}
      <text x={420} y={60} fontSize={13} fill="var(--gd-ink-2)">
        x turns →
      </text>
      <text x={420} y={80} fontSize={13} fill="var(--gd-ink-2)">
        u turns faster →
      </text>
      <text x={420} y={100} fontSize={13} fill="var(--gd-ink-2)">
        y turns too.
      </text>
      <text x={420} y={128} fontSize={13} fill="var(--gd-ink)" fontWeight={600}>
        Rates multiply.
      </text>
    </svg>
  )
}

export default function ChainRule() {
  return (
    <Lesson>
      <Step>
        <H>Functions inside functions</H>
        <P>
          Real models aren’t one simple function. They’re chains: the input goes through one function, whose output goes into
          another, and another. So we need to answer: if <M>{'y = f(g(x))'}</M>, how fast does <M>y</M> change when we nudge{' '}
          <M>x</M>?
        </P>
        <P>
          Think of gears. If gear <Hl c="blue">x</Hl> turns gear <Hl c="green">u</Hl> three times as fast, and u turns gear{' '}
          <Hl c="amber">y</Hl> half as fast as itself, then y turns <M>{'3 \\times \\tfrac{1}{2} = 1.5'}</M> times as fast as x.
          The rates <strong>multiply</strong>.
        </P>
        <Figure n="4.4" caption="Linked gears: each one’s speed is the previous speed times a ratio. Derivatives chain the same way.">
          <Gears />
        </Figure>
      </Step>

      <Step>
        <H>Watch a nudge travel</H>
        <P>
          Take <M>{'u = 3x + 1'}</M> and <M>{'y = u^2'}</M>. Nudge x a little. That nudge is tripled on its way to u (because{' '}
          <M>{'du/dx = 3'}</M>), then multiplied again by <M>{'dy/du = 2u'}</M> on its way to y.
        </P>
        <Figure n="4.5" caption="A small nudge in x, magnified by each link in the chain. The total magnification is the product of the link slopes.">
          <ChainNudge />
        </Figure>
      </Step>

      <Step gate="nq-chain">
        <H>The chain rule</H>
        <MB>{'\\frac{dy}{dx} = \\amber{\\frac{dy}{du}} \\cdot \\green{\\frac{du}{dx}}'}</MB>
        <P>
          In words: <strong>the slope of a chain is the product of the slopes of its links</strong> — each one evaluated at the
          point you’re actually at. It looks like the <M>du</M>’s cancel, and that’s a handy way to remember it.
        </P>
        <Exercise id="chain-check" title="Chain rule vs. reality" {...EX.chainCheck} />
        <NumberQuiz
          id="nq-chain"
          question={<>For <M>{'y = (3x + 1)^2'}</M>, what is <M>{'dy/dx'}</M> at <M>{'x = 1'}</M>?</>}
          answer={24}
          explain={<>u = 3·1 + 1 = 4. Outer slope dy/du = 2u = 8. Inner slope du/dx = 3. Product: 24.</>}
          hint={<>Find u at x = 1 first. Then multiply the outer slope 2u by the inner slope 3.</>}
        />
      </Step>

      <Step gate="chain-build">
        <H>Build two derivatives with it</H>
        <P>
          The second one is important: <M>{'\\sigma'}</M> (sigma) is the <strong>sigmoid</strong>, a squashing function that
          turns any number into something between 0 and 1. You’ll meet it properly in Module 6 — its slope has a beautifully
          simple formula.
        </P>
        <Exercise
          id="chain-build"
          title="sin(x²) and σ(wx + b)"
          {...EX.chainBuild}
          hints={[
            <>
              Outer slope at the inner value, times inner slope: <C>Math.cos(x * x) * 2 * x</C>.
            </>,
            <>
              Compute <C>s = sigmoid(w * x + b)</C>, then return <C>s * (1 - s) * w</C>.
            </>,
          ]}
        />
      </Step>

      <Step gate="q-long">
        <H>Long chains, and a secret</H>
        <P>A neural network is a chain of dozens of functions. The chain rule extends naturally — multiply all the links:</P>
        <MB>{'\\frac{dL}{dx} = \\frac{dL}{dh_3}\\cdot\\frac{dh_3}{dh_2}\\cdot\\frac{dh_2}{dh_1}\\cdot\\frac{dh_1}{dx}'}</MB>
        <P>
          Here’s the secret that makes training possible: you can compute this product <strong>starting from the end</strong>{' '}
          (the loss) and working backwards, reusing each partial product as you go. One backward sweep gives you the slope with
          respect to <em>every</em> parameter at once. That’s called <strong>backpropagation</strong>, and it’s just the chain
          rule, done efficiently. You’ll implement it in Module 7.
        </P>
        <Quiz
          id="q-long"
          question="A chain has 20 links, and each link’s slope is about 0.5. Roughly what is the slope of the whole chain?"
          options={[
            { label: 'About 10', why: 'Slopes multiply, they don’t add: 0.5 × 0.5 × … twenty times.' },
            { label: 'About 0.000001', correct: true },
            { label: 'About 0.5', why: 'Each link shrinks the nudge further.' },
          ]}
          explain="0.5²⁰ ≈ 0.00000095. This is the ‘vanishing gradient’ problem that made deep networks hard to train for years — and why modern designs add shortcuts (residual connections) that you’ll meet in the transformer module."
        />
        <Callout kind="why">
          The chain rule is the single most important formula in deep learning. Every time you hear “the model was trained”,
          the chain rule was applied trillions of times.
        </Callout>
      </Step>
    </Lesson>
  )
}
